/**
 * Where is it.
 *
 * One screen that answers every question an applicant currently has to chase an
 * office to answer: what the period is, when it started, when it ends, when a
 * deemed clearance would arise, which clearances were sought and from whom, how
 * far the procedure has got, who is holding the file and for how long, and
 * whether anything is wanted from the applicant.
 */

import { useCallback, useEffect, useState } from "react";
import { bi, statute } from "@ksidc/ontology";
import { useLang, useSession } from "@ksidc/client";
import type {
  ApplicationResponse,
  CirculationResponse,
  QueriesResponse,
} from "../api-types.js";
import {
  BOARD_NAMES,
  RESPONSE_KIND_NAMES,
  ROUTING_TEST_NAMES,
  days,
  formatDate,
} from "../format.js";
import { HOME, messagesHref, navigate } from "../router.js";
import { Documents } from "../components/Documents.js";
import { Inspection } from "../components/Inspection.js";
import { GeneratedBanner } from "../components/GeneratedBanner.js";
import { StageList } from "../components/StageList.js";
import { StatutoryPanel } from "../components/StatutoryPanel.js";
import { Badge, Callout, EmptyState, ErrorNote, Loading, Panel, ProvenanceRow } from "../components/ui.js";

const BAND_TONE: Record<string, "plain" | "warn" | "alarm" | "calm"> = {
  BREACHED: "alarm",
  DUE_SOON: "warn",
  PAUSED: "warn",
  RUNNING: "plain",
  DECIDED: "calm",
};

export function Status({ id }: { id: string }): JSX.Element {
  const { api } = useSession();
  const { t } = useLang();

  const [data, setData] = useState<ApplicationResponse | null>(null);
  const [circulation, setCirculation] = useState<CirculationResponse | null>(null);
  const [queries, setQueries] = useState<QueriesResponse | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      const app = await api.get<ApplicationResponse>(`/applications/${id}`);
      setData(app);
      setError(null);
      const [circ, q] = await Promise.allSettled([
        api.get<CirculationResponse>(`/workflow/${id}/circulation`),
        api.get<QueriesResponse>(`/workflow/${id}/queries`),
      ]);
      // A file whose workflow has not started yet answers with nothing rather
      // than with an error, so a failure here shows the empty state and not a
      // spinner that never stops.
      setCirculation(
        circ.status === "fulfilled"
          ? circ.value
          : { circulation: [], provenance: statute("r.5, s.3(3), s.4(3), s.5(3)") },
      );
      setQueries(q.status === "fulfilled" ? q.value : null);
    } catch (e) {
      setError(e);
    }
  }, [api, id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <div className="stack">
        <ErrorNote error={error} />
        <button type="button" className="btn btn-secondary" onClick={() => navigate(HOME)}>
          {t(bi("Back to my applications", "എന്റെ അപേക്ഷകളിലേക്ക് മടങ്ങുക"))}
        </button>
      </div>
    );
  }

  if (!data) {
    return <Loading what={bi("Reading your application", "നിങ്ങളുടെ അപേക്ഷ വായിക്കുന്നു")} />;
  }

  const { file, formA, clearances } = data;
  const name =
    (typeof formA["enterprise.entityName"] === "string" && formA["enterprise.entityName"]) ||
    (typeof formA["general.projectName"] === "string" && formA["general.projectName"]) ||
    "";
  const openQueries = queries?.queries.filter((q) => q.open) ?? [];

  return (
    <div className="stack">
      <div>
        <p className="eyebrow">{t(bi("Your application", "നിങ്ങളുടെ അപേക്ഷ"))}</p>
        <h1>{name || t(bi("Application", "അപേക്ഷ"))}</h1>
        <p className="reference">{file.referenceNumber}</p>
        <p style={{ marginBottom: 0 }}>
          <Badge text={file.attention.label} tone={BAND_TONE[file.attention.band] ?? "plain"} />
        </p>
        <p className="muted">
          {t(
            bi(
              `Filed on ${formatDate(file.submittedAt, "en")}.`,
              `${formatDate(file.submittedAt, "ml")}-ന് നൽകി.`,
            ),
          )}
        </p>
      </div>

      <GeneratedBanner provenance={file.provenance} />

      <StatutoryPanel file={file} />

      <Panel title={bi("What you need to do", "നിങ്ങൾ ചെയ്യേണ്ടത്")}>
        {openQueries.length > 0 ? (
          <>
            <Callout
              tone="warn"
              title={bi(
                "The Board has asked you a question",
                "ബോർഡ് നിങ്ങളോട് ഒരു ചോദ്യം ചോദിച്ചിട്ടുണ്ട്",
              )}
              provenance={queries?.pauseRule ?? null}
            >
              <p style={{ marginBottom: 0 }}>
                {t(
                  bi(
                    "The days stop being counted until you answer. They start again on the day your answer reaches the Board.",
                    "നിങ്ങൾ മറുപടി നൽകുന്നതുവരെ ദിവസങ്ങൾ കണക്കാക്കുന്നില്ല. നിങ്ങളുടെ മറുപടി ബോർഡിന് ലഭിക്കുന്ന ദിവസം കണക്കാക്കൽ പുനരാരംഭിക്കും.",
                  ),
                )}
              </p>
            </Callout>
            <div className="btn-row no-print" style={{ marginTop: "1rem" }}>
              <a className="btn" href={messagesHref(id)}>
                {t(bi("Read it and answer", "വായിച്ച് മറുപടി നൽകുക"))}
              </a>
            </div>
          </>
        ) : (
          <p>
            {t(
              bi(
                "You do not need to do anything right now. If the Board asks you a question, you will get a message here and the days will stop being counted.",
                "ഇപ്പോൾ നിങ്ങളിൽ നിന്ന് ഒന്നും ആവശ്യമില്ല. ബോർഡ് എന്തെങ്കിലും ചോദിച്ചാൽ ഇവിടെ ഒരു സന്ദേശം ലഭിക്കും, ദിവസങ്ങൾ കണക്കാക്കുന്നത് നിൽക്കുകയും ചെയ്യും.",
              ),
            )}
          </p>
        )}

        {file.nextStep ? (
          <>
            <hr className="rule" />
            <p className="eyebrow">{t(bi("What the office does next", "ഓഫീസ് അടുത്തതായി ചെയ്യുന്നത്"))}</p>
            <p style={{ marginBottom: 0 }}>{t(file.nextStep.label)}</p>
            <ProvenanceRow items={[file.nextStep.provenance]} />
          </>
        ) : null}
      </Panel>

      <Panel title={bi("Which Board decides it, and why", "ഏത് ബോർഡാണ് തീരുമാനിക്കുന്നത്, എന്തുകൊണ്ട്")}>
        <h3 style={{ marginTop: 0 }}>{t(BOARD_NAMES[file.routing.boardType])}</h3>
        <p className="muted">
          {t(ROUTING_TEST_NAMES[file.routing.test] ?? bi(file.routing.test, file.routing.test))}
        </p>
        <p>{t(file.routing.reason)}</p>
        <ProvenanceRow items={[file.routing.provenance]} />

        <hr className="rule" />

        <p className="eyebrow">{t(bi("Who runs that Board's meetings", "ആ ബോർഡ് വിളിച്ചുചേർക്കുന്നത് ആരാണ്"))}</p>
        <p style={{ marginBottom: 0 }}>{t(file.convenor.designation)}</p>
        <ProvenanceRow items={[file.convenor.provenance]} />
      </Panel>

      <Panel title={bi("The clearances you asked for", "നിങ്ങൾ ആവശ്യപ്പെട്ട അനുമതികൾ")}>
        {clearances.length === 0 ? (
          <EmptyState
            title={bi("None were recorded on this application", "ഈ ഫയലിൽ ഒന്നും രേഖപ്പെടുത്തിയിട്ടില്ല")}
            body={bi(
              "No clearances were listed in section XIII of your Form A. The board office can add them for you.",
              "ഫോറം എ വിഭാഗം XIII പ്രകാരം ഈ അപേക്ഷയിൽ ഒരു അനുമതിയും രേഖപ്പെടുത്തിയിട്ടില്ല. ബോർഡ് ഓഫീസിന് അവ ചേർക്കാം.",
            )}
          />
        ) : (
          <ul className="items">
            {clearances.map((c) => (
              <li className="item" key={c.clearanceTypeId}>
                <div className="item-title">{t(c.name)}</div>
                <div className="item-sub">
                  {t(bi("Issued by", "നൽകുന്നത്"))}: {t(c.authority)}
                </div>
                {c.statutoryTimelineDays ? (
                  <div className="item-sub notice-inline">
                    {t(
                      bi(
                        `This one has its own time limit, set by a different law: ${days(c.statutoryTimelineDays).en}. The Board's 30 days shown above does not apply to it, so do not use the date above for this clearance.`,
                        `ഇതിന് മറ്റൊരു നിയമം നിശ്ചയിച്ച സ്വന്തം സമയപരിധിയുണ്ട്: ${days(c.statutoryTimelineDays).ml}. മുകളിൽ കാണിച്ച ബോർഡിന്റെ 30 ദിവസം ഇതിന് ബാധകമല്ല. അതിനാൽ ഈ അനുമതിക്ക് മുകളിലെ തീയതി ഉപയോഗിക്കരുത്.`,
                      ),
                    )}
                    {c.governingEnactment ? (
                      <div className="tiny muted">{c.governingEnactment}</div>
                    ) : null}
                  </div>
                ) : c.publishedTimelineDays !== null ? (
                  <div className="item-sub">
                    {t(
                      bi(
                        `This office says it usually takes ${days(c.publishedTimelineDays).en}. That is their own target, not the time limit set by law.`,
                        `ഈ ഓഫീസ് സാധാരണ ${days(c.publishedTimelineDays).ml} എടുക്കുമെന്ന് പറയുന്നു. അത് അവരുടെ സ്വന്തം ലക്ഷ്യമാണ്, നിയമം നിശ്ചയിച്ച സമയപരിധിയല്ല.`,
                      ),
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title={bi("How far it has got", "എത്രത്തോളം എത്തി")}>
        <StageList stages={file.stages} />
      </Panel>

      <Panel
        title={bi("Which offices have it now", "ഇത് ആരുടെ പക്കലാണ്")}
        provenance={circulation?.provenance ?? null}
      >
        {circulation === null ? (
          <Loading />
        ) : circulation.circulation.length === 0 ? (
          <EmptyState
            title={bi("Not sent to any office yet", "ഇതുവരെ വിതരണം ചെയ്തിട്ടില്ല")}
            body={bi(
              "The Board has not yet sent your application to the offices on it. Once it does, this page will list each office and how many days it has had your application.",
              "ബോർഡിൽ അംഗങ്ങളായ ഓഫീസുകൾക്ക് നിങ്ങളുടെ അപേക്ഷ ഇതുവരെ കൈമാറിയിട്ടില്ല. കൈമാറിയാൽ, ഓരോ ഓഫീസും അവർ ഫയൽ എത്ര ദിവസം കൈവശം വച്ചിരിക്കുന്നു എന്നതും ഇവിടെ കാണാം.",
            )}
          />
        ) : (
          <ul className="items">
            {circulation.circulation.map((entry) => (
              <li className="item" key={entry.officeId}>
                <div className="item-title">{t(entry.designation)}</div>
                <div className="item-sub">
                  {t(
                    bi(
                      `Sent to them on ${formatDate(entry.circulatedAt, "en")}. Held for ${days(entry.heldForDays).en}.`,
                      `${formatDate(entry.circulatedAt, "ml")}-ന് അയച്ചു. ${days(entry.heldForDays).ml} കൈവശം വച്ചു.`,
                    ),
                  )}
                </div>
                {entry.response ? (
                  <>
                    <div className="item-sub">
                      <strong>
                        {t(
                          RESPONSE_KIND_NAMES[entry.response.kind] ??
                            bi(entry.response.kind, entry.response.kind),
                        )}
                      </strong>{" "}
                      —{" "}
                      {t(
                        bi(
                          `received ${formatDate(entry.response.receivedAt, "en")}`,
                          `${formatDate(entry.response.receivedAt, "ml")}-ന് ലഭിച്ചു`,
                        ),
                      )}
                    </div>
                    <p className="item-sub">{t(entry.response.body)}</p>
                    <ProvenanceRow items={[entry.response.provenance]} />
                  </>
                ) : (
                  <div className="item-sub">
                    <Badge
                      text={bi("Has not come back yet", "ഇതുവരെ മറുപടി വന്നിട്ടില്ല")}
                      tone="warn"
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Inspection applicationId={id} />

      <Documents applicationId={id} />

      <div className="btn-row no-print">
        <a className="btn btn-secondary" href={messagesHref(id)}>
          {t(bi("Messages", "സന്ദേശങ്ങൾ"))}
        </a>
        <button type="button" className="btn btn-secondary" onClick={() => window.print()}>
          {t(bi("Print this page", "ഈ താൾ പ്രിന്റ് ചെയ്യുക"))}
        </button>
      </div>

      <p className="muted">
        {t(
          bi(
            "Every date and count on this page is worked out from what has been recorded about your application. No one types these numbers in by hand.",
            "ഈ താളിലുള്ളതെല്ലാം നിങ്ങളുടെ ഫയലിൽ രേഖപ്പെടുത്തിയതിൽ നിന്ന് കണക്കാക്കിയതാണ്. ആരെങ്കിലും ടൈപ്പ് ചെയ്ത ഒരു സംഖ്യയും ഇവിടെ സൂക്ഷിച്ചിട്ടില്ല.",
          ),
        )}
      </p>
    </div>
  );
}
